<template>
	<Teleport to="body">
		<Transition>
			<aside
				v-if="is_open"
				class="modal-bg"
				@click="
					close_dropdown();
					emit('close');
				"
			>
				<div ref="el" class="modal" role="dialog" aria-modal="true" :aria-labelledby="title_id" @click.stop>
					<header class="modal-header">
						<button
							ref="close_button_el"
							class="btn btn-close"
							@click="
								close_dropdown();
								emit('close');
							"
						>
							<v-icon slug="xmark" />
							<span class="screen-reader-text">Close modal</span>
						</button>
					</header>

					<div class="modal-content">
						<div class="row">
							<div class="salary-age-container">
								<div class="bubble green salary">
									<span>{{ item.salary ? item.salary : 'Salary Unstated' }}</span>
								</div>

								<span v-if="item.age" class="age">
									<v-icon slug="clock" />
									<span>{{ item.age }}</span>
								</span>
							</div>

							<h2 :id="title_id">
								<a :href="item.canonical_url" class="Activate-Slideout-Job-Title">
									{{ item.title }}
								</a>
							</h2>

							<div class="company-details">
								<company-logo :item="item" />

								<div>
									<p v-if="item.company_name" class="company-name">
										<span>{{ item.company_name }}</span>
									</p>

									<p v-if="item.location" class="company-location">
										<v-icon slug="location-dot" />
										<span>{{ item.location }}</span>
									</p>
								</div>
							</div>

							<div class="bubbles">
								<span
									v-for="schedule of item.display_work_schedules"
									:key="schedule"
									class="bubble orange small"
								>
									<span>{{ schedule.replace(/\((.+?)\)/, '') }}</span>
								</span>

								<span
									v-for="employment_type of item.display_employment_types"
									:key="employment_type"
									class="bubble orange small"
								>
									<span>{{ employment_type.replace(/\((.+?)\)/, '') }}</span>
								</span>

								<span v-if="item.display_work_environment" class="blue bubble small">
									<span>{{ item.display_work_environment }}</span>
								</span>
							</div>

							<div class="analyze-resume-banner">
								<div class="analyze-resume-banner-left">
									<h3>✨ Apply 10x Faster</h3>

									<p>
										Analyze your resume for missing keywords, then one-click optimize it. Don't be
										anything less than a <strong>100% match candidate</strong>.
									</p>
								</div>

								<div class="analyze-resume-banner-right">
									<span class="free">Free</span>

									<button type="button" class="btn btn-solid" @click="open_popup(item)">
										Analyze Resume Match
									</button>

									<p class="no-credit-card">No Credit Card Required</p>
								</div>
							</div>

							<div v-if="item.summary" class="job-summary">
								<h3>
									<v-icon slug="clipboard-list" />
									<span>Summary</span>
								</h3>

								<div>
									<p>{{ item.summary }}</p>
								</div>
							</div>

							<div v-if="item.description" class="job-description">
								<h3>Job Description</h3>

								<div v-html="item.description.replace(/<p[^>]*>(\s|&nbsp;|\u00a0)*<\/p>/gi, '')" />
							</div>
						</div>
					</div>

					<footer class="modal-footer">
						<div :class="{ 'job-apply': true, open: dropdown_is_open }">
							<button
								type="button"
								class="Activate-Slideout-Apply-Now btn btn-solid"
								@click="dropdown_is_open ? close_dropdown() : open_dropdown()"
							>
								<span>Apply on Employer Site</span>
								<v-icon slug="angle-down" />
							</button>

							<div class="dropdown">
								<p>Boost your chances before you apply.</p>

								<ul>
									<li>
										<div class="analyze-resume-button">
											<div>
												<span class="label">✨ Apply 10x Faster</span>
												<span class="free">Free</span>
											</div>

											<p class="description">
												It takes <strong>30+ tailored applications</strong> to land jobs like
												this one. We'll help you get that done in <strong>1 hour</strong>.
											</p>

											<button type="button" class="btn btn-solid" @click="open_popup(item)">
												Analyze Resume Match
											</button>

											<p class="no-credit-card">No Credit Card Required</p>
										</div>
									</li>

									<li>
										<a
											:href="item.url"
											class="Apply_External_ATS_Click btn btn-proceed btn-trigger-review"
											target="_blank"
											rel="noopener noreferrer"
											@click="
												close_dropdown();
												emit(`close`);
												maybe_open_review_popup($event, item.url);
											"
										>
											<v-icon slug="arrow-up-right-from-square" />

											<span class="label">
												<strong>Proceed to Application</strong>
												<span>Go directly to the company's job page to apply.</span>
											</span>
										</a>
									</li>
								</ul>
							</div>
						</div>
					</footer>
				</div>
			</aside>
		</Transition>
	</Teleport>
</template>

<style lang="scss" scoped>
@use 'sass:math';

@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

.modal-bg {
	position: fixed;
	background-color: rgba($black, 0.4);
	top: var(--wp-admin--admin-bar--height, 0px);
	left: 0;
	bottom: 0;
	right: 0;
	z-index: 999;

	&.v-enter-active,
	&.v-leave-active {
		transition: all 0.3s ease-in-out;

		.modal {
			transition: inherit;
		}
	}

	&.v-enter-from,
	&.v-leave-to {
		opacity: 0;

		.modal {
			transform: translateX(100%);
			opacity: 0;
			visibility: hidden;
		}
	}
}

.modal {
	width: 48em;
	max-width: 90%;
	background-color: $white;
	position: fixed;
	top: var(--wp-admin--admin-bar--height, 0px);
	right: 0;
	border-radius: 1.25em 0 0 1.25em;
	display: flex;
	bottom: 0;
	flex-direction: column;
}

@include media('<=phone') {
	.modal {
		max-width: 100%;
		top: calc(var(--wp-admin--admin-bar--height, 0px) + $spacer-block);
		border-radius: 1.25em 1.25em 0 0;
	}
}

.modal-header {
	flex: 0 0 auto;
	padding-top: $spacer;
	padding-bottom: math.div($spacer, 2);
	display: flex;
	justify-content: flex-end;
	padding-left: $spacer;
	padding-right: $spacer;
}

.modal-content {
	flex: 1 1 auto;
	overflow-y: scroll;
	padding-left: $spacer;
	padding-right: $spacer;
	min-height: 0;
}

.salary-age-container {
	display: flex;
	align-items: center;
	gap: $spacer;
	margin-bottom: 1rem;
}

h2 {
	@include h3;
	border-bottom: 1px solid $grey-border;
	padding-bottom: 1rem;
	margin-bottom: 1rem;
}

.company-details {
	display: flex;
	align-items: center;
	gap: math.div($spacer, 2);
	margin-bottom: 1rem;

	> img {
		display: block;
		flex: 0 0 auto;
		border: 2px solid $grey-border;
		border-radius: 0.5em;
	}
}

.company-name {
	margin-bottom: 0;
	font-weight: bold;
	color: $black;
	font-size: 0.875rem;
}

.company-location {
	font-size: 0.75rem;
	display: flex;
	align-items: center;
	gap: 0.25rem;

	svg {
		width: 1rem;
		height: 1rem;
		flex: 0 0 auto;
		display: block;
	}
}

.bubbles {
	display: flex;
	align-items: center;
	gap: 0.25em;
	flex-wrap: wrap;
	margin-bottom: 1rem;

	&:last-child {
		margin-bottom: 0;
	}
}

.job-summary,
.job-description {
	margin-bottom: $spacer;
	color: $black;

	&:last-child {
		margin-bottom: 0;
	}

	> h3 {
		display: flex;
		align-items: center;
		margin-bottom: 1rem;
		gap: ($spacer * 0.5);
		font-size: rfs(1.125, 1.25);

		svg {
			display: block;
			flex: 0 0 auto;
			width: 1.25rem;
			height: 1.25rem;
		}
	}
}

.job-summary {
	padding: $spacer;
	border: 1px solid $grey-border;
	border-radius: 1.25em;
	background-color: #fffcf7;
}

.job-description {
	> :deep(div) {
		@include entry-content;

		h4 {
			font-size: rfs(1, 1.125);
		}

		h5 {
			font-size: 1em;
		}

		h6 {
			font-size: 1em;
		}

		ul,
		ol,
		p {
			font-size: rfs(1, 1.125);
		}
	}
}

.modal-footer {
	flex: 0 0 auto;
	padding-left: $spacer;
	padding-right: $spacer;
	background-color: $bg;
	border-top: 1px solid $grey-border;
	padding-bottom: math.div($spacer, 2);
	padding-top: math.div($spacer, 2);
	border-bottom-left-radius: 1.25em;
}

.job-apply {
	position: relative;

	&.open {
		> .btn-solid {
			> svg {
				transform: scaleY(-1);
			}
		}

		.dropdown {
			visibility: visible;
			opacity: 1;
		}
	}

	.dropdown {
		visibility: hidden;
		position: absolute;
		bottom: 100%;
		left: 0;
		width: 24em;
		max-width: 100%;
		margin-bottom: math.div($spacer, 2);
		padding: 1rem;
		border: 1px solid $grey-border;
		border-radius: 1em;
		background-color: $white;
		opacity: 0;
		transition:
			visibility 0.3s,
			opacity 0.3s ease-in-out;

		> p {
			margin-bottom: math.div($spacer, 2);
			color: $black;
			font-weight: 600;
		}

		ul {
			li {
				margin-bottom: math.div($spacer, 2);

				&:last-child {
					margin-bottom: 0;
				}
			}
		}

		.btn-proceed {
			display: flex;
			padding: 1rem;
			gap: math.div($spacer, 2);
			border-radius: math.div($spacer, 2);
			text-align: left;

			> svg {
				flex: 0 0 auto;
				width: 1rem;
				height: 1rem;
			}

			.label {
				strong,
				span {
					@include type;
					display: block;
					line-height: 1.15;
				}

				strong {
					margin-bottom: 0.25rem;
				}

				span {
					font-size: 0.875rem;
				}
			}
		}

		.btn-proceed {
			border: 1px solid $grey-border;

			&:hover {
				background-color: $bg;
			}
		}
	}
}

@include media('<=phone') {
	.modal-footer {
		border-bottom-left-radius: 0;
	}
}
</style>

<script setup>
import { onKeyStroke } from '@vueuse/core';
import { maybe_open_review_popup } from 'reviews';
import { open_popup } from 'rh-widget';
import { nextTick, ref, useId, watch } from 'vue';

import CompanyLogo from './_company-logo.vue';
import VIcon from './_v-icon.vue';

const props = defineProps({
	item: {
		type: Object,
		required: true,
	},
	is_open: {
		type: Boolean,
		required: true,
	},
});

const emit = defineEmits([`close`]);

const title_id = useId();

const close_button_el = ref(null);

const dropdown_is_open = ref(false);

let previously_focused_el = null;

function open_dropdown() {
	dropdown_is_open.value = true;
}

function close_dropdown() {
	dropdown_is_open.value = false;
}

watch(
	() => props.is_open,
	(is_open) => {
		if (is_open) {
			previously_focused_el = document.activeElement;

			nextTick(() => close_button_el.value?.focus());

			return;
		}

		previously_focused_el?.focus();
	},
);

onKeyStroke(`Escape`, (e) => {
	if (!props.is_open) {
		return;
	}

	e.preventDefault();

	close_dropdown();

	emit(`close`);
});
</script>
